import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ZoomIn, ClipboardList } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/components/site/Seo";
import { assetUrl, lines } from "@/lib/api";
import { Rich, Reveal } from "@/components/site/Rich";
import { PageBand } from "@/components/site/PageBand";
import { Lightbox } from "@/components/site/Lightbox";

const SampleImage = ({ cat, onOpen, className = "" }) => (
  <button type="button" onClick={() => onOpen(assetUrl(cat.image))} className={`group relative block mx-auto ${className}`} style={{ width: `${cat.image_width || 100}%` }} data-testid={`category-image-${cat.slug}`}>
    <img src={assetUrl(cat.image)} alt={`${cat.title} sample`} className="w-full h-auto rounded-lg shadow-lg" />
    <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-charcoal opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn className="h-3.5 w-3.5" />Enlarge</span>
  </button>
);

const CategoryText = ({ cat }) => (
  <div>
    <h2 className="font-serif font-bold text-3xl sm:text-4xl text-brand" data-testid={`category-title-${cat.slug}`}>{cat.title}</h2>
    <p className="mt-5 leading-relaxed text-charcoal/80">{cat.description}</p>
    <p className="mt-7 font-bold text-charcoal">{cat.highlights_label}</p>
    <ul className="mt-3 grid gap-2 sm:grid-cols-2">
      {lines(cat.highlights).map((h, i) => <li key={i} className="flex gap-2 text-sm text-charcoal/80"><CheckCircle2 className="h-4 w-4 mt-0.5 text-brass shrink-0" />{h}</li>)}
    </ul>
    <div className="mt-8 flex flex-wrap items-center gap-4">
      <div className="rounded-xl border-2 border-brass bg-white px-6 py-4" data-testid={`category-price-${cat.slug}`}>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brass">{cat.price_label}</p>
        <p className="mt-1 font-serif text-xl font-bold text-charcoal">{cat.price}</p>
      </div>
      <Link to="/listing-questionnaire" className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-dark text-white px-6 py-3 text-sm font-bold transition-colors" data-testid={`category-button-${cat.slug}`}>
        <ClipboardList className="h-4 w-4" />{cat.button}
      </Link>
    </div>
  </div>
);

const Category = ({ cat, index, onOpen }) => {
  const wide = index < 2;
  return (
    <section id={cat.slug} className={index % 2 ? "bg-ivory-alt" : "bg-white"} data-testid={`category-${cat.slug}`}>
      <Reveal className="max-w-[1180px] mx-auto px-6 py-16 lg:py-20">
        {wide || !cat.image ? (
          <>
            <CategoryText cat={cat} />
            {cat.image && <SampleImage cat={cat} onOpen={onOpen} className="mt-10" />}
          </>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
            <CategoryText cat={cat} />
            <SampleImage cat={cat} onOpen={onOpen} />
          </div>
        )}
      </Reveal>
    </section>
  );
};

export default function ListingOptions() {
  const { content } = useSite();
  const l = content.listing;
  const { hash } = useLocation();
  const [open, setOpen] = useState(null);
  const cats = content.categories.items.map((c, i) => ({ ...c, _i: i })).filter((c) => !c.hidden);
  useSeo("listing");

  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="listing-options-page">
      <PageBand title={l.title} id="listing" />
      <section className="max-w-[1180px] mx-auto px-6 py-14">
        <Rich html={l.intro} className="text-charcoal/80 text-[15px]" />
        <h2 className="mt-10 font-serif text-xl sm:text-2xl font-bold text-charcoal">{l.nav_title}</h2>
        <div className="mt-5 flex flex-wrap gap-3" data-testid="category-pill-nav">
          {cats.map((c) => (
            <a key={c.slug} href={`#${c.slug}`} onClick={(e) => { e.preventDefault(); document.getElementById(c.slug)?.scrollIntoView({ behavior: "smooth" }); }}
              className="rounded-full border border-brand/40 bg-white px-5 py-2 text-sm font-semibold text-brand hover:bg-brand hover:text-white transition-colors" data-testid={`category-pill-${c.slug}`}>{c.title}</a>
          ))}
        </div>
      </section>
      {cats.map((c) => <Category key={c.slug} cat={c} index={c._i} onOpen={setOpen} />)}
      <section className="bg-charcoal text-white" data-testid="listing-closing">
        <div className="max-w-[1180px] mx-auto px-6 py-16">
          <h2 className="font-serif font-bold text-2xl sm:text-3xl text-brass">{l.closing_title}</h2>
          <Rich html={l.closing} className="mt-5 text-white/80 [&_a]:text-brass" />
          <Link to="/listing-questionnaire" className="mt-8 inline-flex rounded-full bg-brand hover:bg-brand-dark px-6 py-3 text-sm font-bold transition-colors" data-testid="listing-closing-button">Fill the Online Listing Questionnaire</Link>
        </div>
      </section>
      <Lightbox src={open} onClose={() => setOpen(null)} />
    </div>
  );
}
